import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Avatar,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  LocationOn,
  CalendarMonth,
  Star,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

const STATUS_COLORS = {
  PENDING_REVIEW: "warning",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "default",
};

const getDisplayName = (name) => {
  if (!name) return null;
  if (typeof name === "string") return name;
  return [name.firstName, name.middleName, name.lastName].filter(Boolean).join(" ") || null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateStr));
  } catch {
    return "Invalid date";
  }
};

export default function TestimonialDetailDialog({ open, onClose, testimonial }) {
  const [resolvedPartner, setResolvedPartner] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);

  useEffect(() => {
    if (!testimonial) return;
    const { partner } = testimonial;
    if (!partner) { setResolvedPartner(null); return; }
    if (typeof partner === "object") { setResolvedPartner(partner); return; }
    // String ID — fetch
    setPartnerLoading(true);
  }, [testimonial]);

  if (!testimonial) return null;

  const { user, reviewedBy } = testimonial;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }} p={0} m={0}>
          Testimonial Detail
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {/* Status Chips */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip
            label={testimonial.status}
            color={STATUS_COLORS[testimonial.status] || "default"}
            size="small"
          />
          {testimonial.isPublished && (
            <Chip icon={<Visibility />} label="Published" color="info" size="small" />
          )}
          {!testimonial.isPublished && (
            <Chip icon={<VisibilityOff />} label="Unpublished" size="small" />
          )}
          {testimonial.isTopStory && (
            <Chip icon={<Star />} label="Top Story" color="warning" size="small" />
          )}
          {testimonial.featuredOrder != null && (
            <Chip label={`Featured #${testimonial.featuredOrder}`} color="secondary" size="small" />
          )}
        </Box>

        {/* Couple Photo */}
        {testimonial.couplePhoto?.url && (
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <img
              src={testimonial.couplePhoto.url}
              alt="Couple"
              style={{
                maxWidth: "100%",
                maxHeight: 280,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
          </Box>
        )}

        {/* Video */}
        {testimonial.video?.url && (
          <Box sx={{ mb: 2, textAlign: "center" }}>
            <video
              src={testimonial.video.url}
              controls
              style={{
                maxWidth: "100%",
                maxHeight: 320,
                borderRadius: 8,
                backgroundColor: "#000",
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {testimonial.video.mimeType} &nbsp;·&nbsp;{" "}
              {(testimonial.video.sizeBytes / (1024 * 1024)).toFixed(1)} MB
              {testimonial.video.durationSeconds
                ? ` · ${testimonial.video.durationSeconds}s`
                : ""}
            </Typography>
          </Box>
        )}

        {/* Title & Story */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }} p={0} m={0}>
          {testimonial.title || "Untitled"}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-wrap", lineHeight: 1.7 }} p={0} m={0}>
          {testimonial.story}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {/* Wedding Info */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" p={0} m={0}>
                <strong>Wedding Date:</strong>{" "}
                {testimonial.weddingDate
                  ? new Date(testimonial.weddingDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2" p={0} m={0}>
                <strong>Location:</strong> {testimonial.weddingLocation || "—"}
              </Typography>
            </Box>
          </Grid>

          {/* Trigger / Credits */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom p={0} m={0}>
              <strong>Trigger:</strong> {testimonial.triggerSource || "—"}
            </Typography>
            <Typography variant="body2" gutterBottom p={0} m={0}>
              <strong>Credits Granted:</strong> {testimonial.rewardCreditsGranted ?? 0}
            </Typography>
            <Typography variant="body2" p={0} m={0}>
              <strong>Badge Granted:</strong> {testimonial.badgeGranted ? "Yes" : "No"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Submitted By + Partner */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }} p={0} m={0}>
              Submitted By
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ width: 36, height: 36 }}>
                {(getDisplayName(user?.name) || user?.username || "?")[0]}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }} p={0} m={0}>
                  {getDisplayName(user?.name) || user?.username || "Unknown"}
                </Typography>
                <Typography variant="caption" color="text.secondary" p={0} m={0}>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {(resolvedPartner || partnerLoading) && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }} p={0} m={0}>
                Partner
              </Typography>
              {partnerLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 36, height: 36 }}>
                    {(getDisplayName(resolvedPartner?.name) || resolvedPartner?.username || "P")[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} p={0} m={0}>
                      {getDisplayName(resolvedPartner?.name) || resolvedPartner?.username || "Unknown"}
                    </Typography>
                    {resolvedPartner?.email && (
                      <Typography variant="caption" color="text.secondary" p={0} m={0}>
                        {resolvedPartner.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
        <Box sx={{ mb: 2 }} />

        {/* Reviewed By */}
        {reviewedBy && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }} p={0} m={0}>
              Reviewed By
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Avatar sx={{ width: 36, height: 36 }}>
                {(getDisplayName(reviewedBy.name) || reviewedBy.username || "A")[0]}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }} p={0} m={0}>
                  {getDisplayName(reviewedBy.name) || reviewedBy.username}
                </Typography>
                <Typography variant="caption" color="text.secondary" p={0} m={0}>
                  {formatDate(testimonial.reviewedAt)}
                </Typography>
              </Box>
            </Box>
            {testimonial.reviewNote && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }} p={0} m={0}>
                Note: {testimonial.reviewNote}
              </Typography>
            )}
          </>
        )}

        {/* Review History */}
        {testimonial.reviewHistory?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }} p={0} m={0}>
              Review History
            </Typography>
            {testimonial.reviewHistory.map((entry, idx) => (
              <Box key={idx} sx={{ mb: 1, pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                <Typography variant="body2" p={0} m={0}>
                  <strong>{entry.action}</strong> — {formatDate(entry.at)}
                </Typography>
                {entry.note && (
                  <Typography variant="caption" color="text.secondary" p={0} m={0}>
                    {entry.note}
                  </Typography>
                )}
              </Box>
            ))}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" display="block" p={0} m={0}>
          Created: {formatDate(testimonial.createdAt)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" p={0} m={0}>
          Updated: {formatDate(testimonial.updatedAt)}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
